import React, { useState, useEffect, FC } from 'react'
import { localStorageWrapper } from '../helpers/localStorage'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  Avatar,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Alert,
  useTheme,
} from '@mui/material'
import axios from 'axios'
import { DisplayItemKey } from '../types/models/Presc'
import { useLoginMutation, useAuthCheckQuery } from '../features/users'
import { isCertDomain, API_ORIGIN } from '../constants/const'
import { isCertTlsRejection } from '../helpers/isCertTlsRejection'
import { CertRestartGuide } from '../components/molecules/CertRestartGuide'

type EditMode = {
  kana: boolean
  insurance_num: boolean
  birthday: boolean
  allowed_insurance_num_blank: boolean
}

type User = {
  id: string
  pharmacy_name: string
  plan: string
  qr_size: string
  edit_mode: EditMode
  display_items: DisplayItemDict
  app_type: string
  is_annotator: boolean
  is_activated_photo: boolean
  show_save_csv_button: boolean
  is_image_rotatable: boolean
  editable_item_font_size: string
  is_name_able_to_copy: boolean
  is_headquarters: boolean
  use_legacy_ui: boolean
  guide_variant: string
}

type DisplayItemDict = {
  patient_matching: DisplayItemKey[]
  non_matching_main: DisplayItemKey[]
  non_matching_sub: DisplayItemKey[]
  non_matching_other: DisplayItemKey[]
}

type ResponseData = {
  version: null
  user: User
}

export const LoginPage: FC = () => {
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const dispatch = useDispatch()
  const navigate = useNavigate()
  // useRedirectToLogin が持ち越した query(QuickSync の知らせが指す `?event=`)。ログイン後に `/` へ付け直す
  const { search } = useLocation()
  const [errormessage, setErrormessage] = useState<string>('')
  const theme = useTheme()

  const [certError, setCertError] = useState<boolean>(false)

  const loginMutation = useLoginMutation()
  const { refetch: refetchAuth } = useAuthCheckQuery({ enabled: false })

  // cert環境ではページマウント時にAPIへの疎通を確認し、
  // TLS拒否（証明書未選択/失効/非信頼）の場合は証明書の案内を表示する。
  // cert環境のTLS拒否は useShowMaintenanceModal がメンテ扱いから除外するため、
  // この警告がMaintenanceModalに覆われずログイン画面上に出る。
  useEffect(() => {
    if (!isCertDomain) return

    axios
      .get(`${API_ORIGIN}/`, { withCredentials: true, timeout: 5000 })
      .catch((error: unknown) => {
        // 無応答 = TLS層で拒否（証明書なし）。応答あり（302等）= 到達済み、証明書OK
        if (isCertTlsRejection(error)) {
          setCertError(true)
        }
      })
  }, [])

  const onSuccess = (responseData: ResponseData) => {
    dispatch({
      type: 'setIsLoggedIn',
      isLoggedIn: true,
      appType: responseData.user.app_type,
    })
    localStorageWrapper.setItem('qr_size', responseData.user.qr_size)
    localStorageWrapper.setItem(
      'patientMatchingDisplayItems',
      responseData.user.display_items.patient_matching
    )
    localStorageWrapper.setItem(
      'nonMatchingMainDisplayItems',
      responseData.user.display_items.non_matching_main
    )
    localStorageWrapper.setItem(
      'nonMatchingSubDisplayItems',
      responseData.user.display_items.non_matching_sub
    )
    localStorageWrapper.setItem(
      'nonMatchingOtherDisplayItems',
      responseData.user.display_items.non_matching_other
    )
    localStorageWrapper.setItem(
      'isPatientKanaEditable',
      responseData.user.edit_mode.kana
    )
    localStorageWrapper.setItem(
      'isInsuranceNumEditable',
      responseData.user.edit_mode.insurance_num
    )
    localStorageWrapper.setItem(
      'isBirthdayEditable',
      responseData.user.edit_mode.birthday
    )
    localStorageWrapper.setItem(
      'allowedInsuranceNumBlank',
      responseData.user.edit_mode.allowed_insurance_num_blank
    )
    localStorageWrapper.setItem(
      'editableItemFontSize',
      responseData.user.editable_item_font_size
    )
    localStorageWrapper.setItem(
      'isImageRotatable',
      responseData.user.is_image_rotatable
    )
    localStorageWrapper.setItem('app_type', responseData.user.app_type)
    localStorageWrapper.setItem(
      'guide_variant',
      responseData.user.guide_variant
    )
    localStorageWrapper.setItem('is_annotator', responseData.user.is_annotator)
    localStorageWrapper.setItem(
      'is_activated_photo',
      responseData.user.is_activated_photo
    )
    localStorageWrapper.setItem(
      'show_save_csv_button',
      responseData.user.show_save_csv_button
    )
    localStorageWrapper.setItem('userId', responseData.user.id)
    localStorageWrapper.setItem(
      'is_name_able_to_copy',
      responseData.user.is_name_able_to_copy
    )
    localStorageWrapper.setItem(
      'is_headquarters',
      responseData.user.is_headquarters
    )
    localStorageWrapper.setItem(
      'use_legacy_ui',
      responseData.user.use_legacy_ui
    )
  }

  // 証明書の案内（certError）は、送るときに消さず、ログインの結果で決める。
  // 無応答 = TLS層で拒否 = 案内を出す（出ていればそのまま）。
  // 応答あり = 証明書は送れている = 案内を下ろす。ただし 403 cert_mismatch
  // （有効だがこの口座の証明書ではない）は選び直しが要るので出す。
  // 送るときに消すと「ログイン中...」の間だけ消えて失敗で戻る点滅になり、
  // 店舗には「押したら消えた = 直った」に見える。
  const handleLoginError = (e: unknown) => {
    if (isCertTlsRejection(e)) {
      // SPA自体はCloudFrontから起動済み＝一般疎通はあるため、無応答はcert問題の可能性が高い。
      setCertError(true)
      return
    }
    if (!axios.isAxiosError(e)) {
      setErrormessage(
        '予期しないエラーが発生しました。もう一度お試しください。'
      )
      return
    }
    if (!e.response) {
      setErrormessage(
        'ネットワークに接続できていないようです。接続を確認して、もう一度お試しください。'
      )
      return
    }

    const status = e.response.status
    const isCertMismatch =
      status === 403 &&
      e.response.data.error_type === 'wrong_environment' &&
      e.response.data.error_detail === 'cert_mismatch'
    setCertError(isCertMismatch)
    if (isCertMismatch) return

    switch (status) {
      case 400: {
        const remaining_attempts = e.response.data.remaining_attempts
        const cooloff_time = e.response.data.cooloff_time
        setErrormessage(
          `メールアドレスまたはパスワードが正しくない可能性があります。残り${remaining_attempts}回間違えると${cooloff_time}時間ログインできなくなるので、ご注意ください。`
        )
        break
      }
      case 403: {
        if (e.response.data.error_type === 'wrong_environment') {
          const guidance = isCertDomain
            ? 'このアカウントは通常環境専用です。通常環境のURLからログインしてください。'
            : 'このアカウントはcert認証環境専用です。cert環境のURLからログインしてください。'
          setErrormessage(guidance)
        } else {
          setErrormessage(
            '一定回数以上ログインが失敗したため、アカウントへのアクセスが一時的に制限されています。アクセス制限を解除したい場合、または新しい認証情報が必要な場合は、担当者にお問い合わせください。'
          )
        }
        break
      }
      default: {
        setErrormessage(
          '予期しないエラーが発生しました。もう一度お試しください。'
        )
      }
    }
  }

  const submit = () => {
    setErrormessage('')
    if (!validate()) return

    loginMutation.mutate(
      { email, password },
      {
        onSuccess: async () => {
          const { data: authData } = await refetchAuth()
          if (authData?.data) {
            onSuccess(authData.data)
            navigate({ pathname: '/', search })
          }
        },
        onError: handleLoginError,
      }
    )
  }

  const validate = () => {
    if (!email) return false
    if (!password) return false
    return true
  }

  return (
    <Stack
      height="100vh"
      direction="column"
      spacing={3}
      alignItems="center"
      justifyContent="center"
    >
      <Stack direction="column" alignItems="center" spacing={1}>
        <Avatar />
        <Typography
          component="h1"
          variant="h5"
          fontFamily="Hiragino Maru Gothic Pro"
        >
          ログイン
        </Typography>
      </Stack>
      <Box width="400px">
        <form noValidate>
          <Stack direction="column" spacing={3}>
            <Stack direction="column" spacing={1}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label="Emailアドレス"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputLabelProps={{
                  style: { color: theme.palette.secondary.dark },
                }}
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label="パスワード"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputLabelProps={{
                  style: { color: theme.palette.secondary.dark },
                }}
              />
            </Stack>
            <Button
              fullWidth
              variant="contained"
              disabled={loginMutation.isPending}
              sx={{
                bgcolor: theme.palette.primary.dark,
                color: '#fff',
              }}
              onClick={submit}
            >
              {loginMutation.isPending ? 'ログイン中...' : 'ログイン'}
            </Button>
          </Stack>
        </form>
        {certError && (
          <Box pt={2}>
            <CertRestartGuide />
          </Box>
        )}
        {errormessage && (
          <Box pt={2}>
            <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>
              {errormessage}
            </Alert>
          </Box>
        )}
      </Box>
    </Stack>
  )
}
